import React, { FC, useEffect, useState } from 'react';
import { Button as AntButton, Modal, Select } from 'antd';
import FileUpload from '../FileUpload';
import Button from '../Button';
import { useTranslation } from 'react-i18next';
import {
  useLazyGetExcelBookTemplateQuery,
  useLazyGetExcelUploadCountQuery,
  useLazyGetCampusExcelBookTemplateQuery,
  useLazyGetExcelQuantityUploadCountQuery,
} from '../../api/books/bookManagement.api';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { RiFileExcel2Line } from 'react-icons/ri';

interface IProps {
  visible: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  titleText: string;
  confirmText: string;
  cancelText?: string;
  className?: string;
  width?: number;
  file?: any;
  onSetFile?: (file: any) => void;
  isCampusBatchQuantity?: boolean;
}

const BookFileUploadModal: FC<IProps> = ({
  visible,
  onConfirm,
  onCancel,
  titleText,
  confirmText,
  cancelText,
  className = '',
  width = 500,
  file,
  onSetFile,
  isCampusBatchQuantity = false,
}) => {
  const { t, i18n } = useTranslation();
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const [bookUploadTrigger, bookUploadResult] =
    useLazyGetExcelUploadCountQuery();
  const [bookQuantityUploadTrigger, bookQuantityUploadResult] =
    useLazyGetExcelQuantityUploadCountQuery();
  const [templateTrigger, templateResult] = useLazyGetExcelBookTemplateQuery();
  const [campusTemplateTrigger, campusTemplateResult] =
    useLazyGetCampusExcelBookTemplateQuery();
  const [campusCode, setCampusCode] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (file) {
      let formData = new FormData();
      formData.append('excel', file);
      !isCampusBatchQuantity
        ? bookUploadTrigger(formData)
        : bookQuantityUploadTrigger({ data: formData, campusCode });
    }
  }, [file]);

  const excelDownload = (result: any, downloadName: string) => {
    if (result.isSuccess && !result.isFetching) {
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName + '.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    excelDownload(templateResult, 'upload_books');
  }, [templateResult]);

  useEffect(() => {
    excelDownload(campusTemplateResult, 'upload_campus');
  }, [campusTemplateResult]);

  const getFooter = () => {
    if (cancelText) {
      return [
        <div className='comm-btn__area justify-content-center'>
          <AntButton
            key="submit"
            className='btn-basic pink'
            disabled={
              isCampusBatchQuantity
                ? bookQuantityUploadResult.isFetching ||
                  !bookQuantityUploadResult.isSuccess ||
                  bookQuantityUploadResult.data.data <= 0
                : bookUploadResult.isFetching ||
                  !bookUploadResult.isSuccess ||
                  bookUploadResult.data.data <= 0
            }
            onClick={onConfirm}
          >
            {confirmText}
          </AntButton>
          <AntButton key="cancel" className='btn-basic gray' onClick={onCancel}>
            {cancelText}
          </AntButton>
        </div>
      ];
    } else {
      return [
        <AntButton key="submit" className='btn-basic pink' onClick={onConfirm}>
          {confirmText}
        </AntButton>
      ];
    }
  };

  useEffect(() => {
    if (campusCode) return;

    let _campusCode = campusData.filter(
      (item) => item.campusCode !== 'CB00000001',
    )[0]?.campusCode;
    setCampusCode(_campusCode);
  }, [campusData]);

  return (
    <Modal
      visible={visible}
      title={titleText}
      onCancel={onCancel}
      width={width}
      footer={getFooter()}
    >
      {isCampusBatchQuantity && (
        <div className="flex justify-content-center align-items-center mb-2">
          <div className="mr-2">{t('books.text.3')}</div>
          <div className="w-25">
            <Select
              className="w-100"
              placeholder={t('class.class.placeholder1')}
              value={campusCode}
              onChange={(value) => setCampusCode(value)}
            >
              {campusData
                ?.filter((item) => item.campusCode !== 'CB00000001')
                ?.map((item) => (
                  <Select.Option key={item.campusCode} value={item.campusCode}>
                    {i18n.language === 'ko'
                      ? item.campusKorName
                      : item.campusEngName}
                  </Select.Option>
                ))}
            </Select>
          </div>
        </div>
      )}

      <div className="flex justify-content-center mb-2">
        <div>
          <FileUpload
            file={file}
            setFile={(file: any) => onSetFile && onSetFile(file)}
            accept={'.xls, .xlsx'}
          />
        </div>
        <div className="ml-1">
          <Button
            className="btn-basic green"
            isLoading={templateResult.isFetching}
            onClick={() => {
              !isCampusBatchQuantity
                ? templateTrigger()
                : campusTemplateTrigger({ campusCode });
            }}
            text={t('books.text.4')}
            useExcelIcon
          />
        </div>
      </div>
      <div className={className + 'flex justify-content-center'}>
      {bookUploadResult.isFetching || bookQuantityUploadResult.isFetching ? (
          <img src="/images/loading.svg" width="64" alt="Spinner" />
        ) : bookUploadResult.isSuccess ? (
          t('booksManagement.excel.valid').replace(
            '%s',
            bookUploadResult.data.data,
          )
        ) : (
          bookQuantityUploadResult.isSuccess &&
          t('booksManagement.excel.valid').replace(
            '%s',
            bookQuantityUploadResult?.data?.data,
          )
        )}
      </div>
    </Modal>
  );
};

export default BookFileUploadModal;
